import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Target, Plus, Search, Phone, Mail, MapPin, ArrowRight, Flame, Loader2 } from "lucide-react";

const SOURCES = ["Meta Lead Ads", "WhatsApp", "Website Form", "Referral", "Manual"];
const STATUSES = ["new", "contacted", "qualified", "unqualified"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

function LeadForm({ onSubmit, saving }) {
  const [f, setF] = useState({ name: "", company: "", phone: "", email: "", source: "Manual", priority: "medium", status: "new", lead_score: 50, sla_status: "healthy", location: "", meta_page: "", campaign: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name-input" /></div>
        <div className="space-y-1.5"><Label>Company</Label><Input value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="lead-company-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone-input" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Source</Label>
          <Select value={f.source} onValueChange={(v) => set("source", v)}><SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label>Priority</Label>
          <Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <div className="space-y-1.5"><Label>Location</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Bengaluru" data-testid="lead-location-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Meta Page</Label><Input value={f.meta_page} onChange={(e) => set("meta_page", e.target.value)} placeholder="e.g. Certon Logistic" data-testid="lead-meta-page-input" /></div>
        <div className="space-y-1.5"><Label>Campaign</Label><Input value={f.campaign} onChange={(e) => set("campaign", e.target.value)} placeholder="Optional" data-testid="lead-campaign-input" /></div>
      </div>
      <p className="text-xs text-muted-foreground">Matching Lead Routing rules will auto-assign the business unit, team, owner & SLA.</p>
      <DialogFooter>
        <Button onClick={() => onSubmit(f)} disabled={saving || !f.name} data-testid="lead-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create lead"}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Leads() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(params.get("new") === "1");
  const [selected, setSelected] = useState(null);

  const { data: leads = [], isLoading } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const ownerName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";

  const createMut = useMutation({
    mutationFn: (body) => api.post("/leads", body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      setOpen(false);
      const r = res.data?._routing;
      if (r?.assigned_to) toast.success("Lead created & routed", { description: `Assigned to ${r.assigned_to}${r.applied_rule ? ` · ${r.applied_rule}` : ""}` });
      else toast.success("Lead created");
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/leads/${id}`, body),
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["leads"] }); setSelected((s) => s && { ...s, ...v.body }); toast.success("Lead updated"); },
  });
  const convertMut = useMutation({
    mutationFn: (id) => api.post(`/leads/${id}/convert`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); setSelected(null); toast.success("Converted to opportunity 🎉"); },
  });

  const filtered = leads.filter((l) =>
    (status === "all" || l.status === status) &&
    (!q || `${l.name} ${l.company} ${l.email}`.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div>
      <PageHeader title="CRM · Leads" subtitle="One Lead Engine for every source" testid="leads-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-lead-btn"><Plus className="mr-1.5 h-4 w-4" /> New Lead</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create lead</DialogTitle></DialogHeader>
            <LeadForm onSubmit={(f) => createMut.mutate(f)} saving={createMut.isPending} />
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="pl-9" data-testid="leads-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : filtered.length === 0 ? (
        <EmptyState icon={Target} title="No leads yet" description="Create your first lead or connect Meta Lead Ads to start capturing automatically."
          action={<Button onClick={() => setOpen(true)} className="rounded-full" data-testid="empty-create-lead"><Plus className="mr-1.5 h-4 w-4" /> Create Lead</Button>} testid="leads-empty" />
      ) : (
        <Card className="overflow-hidden">
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead><TableHead>Source</TableHead><TableHead>Score</TableHead>
                  <TableHead>Priority</TableHead><TableHead>SLA</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                    <TableCell>
                      <p className="font-medium">{l.name}</p>
                      <p className="text-xs text-muted-foreground">{l.company}</p>
                    </TableCell>
                    <TableCell className="text-sm">{l.source}</TableCell>
                    <TableCell><span className="flex items-center gap-1 text-sm"><Flame className="h-3.5 w-3.5 text-escalation" />{l.lead_score}</span></TableCell>
                    <TableCell><StatusBadge value={l.priority} /></TableCell>
                    <TableCell><StatusBadge value={l.sla_status} /></TableCell>
                    <TableCell><StatusBadge value={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="divide-y divide-border md:hidden">
            {filtered.map((l) => (
              <button key={l.id} onClick={() => setSelected(l)} className="flex w-full items-center justify-between p-4 text-left" data-testid={`lead-card-${l.id}`}>
                <div><p className="font-medium">{l.name}</p><p className="text-xs text-muted-foreground">{l.company} · {l.source}</p></div>
                <StatusBadge value={l.status} />
              </button>
            ))}
          </div>
        </Card>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {selected && (
            <>
              <SheetHeader><SheetTitle>{selected.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex flex-wrap gap-2"><StatusBadge value={selected.status} /><StatusBadge value={selected.priority} /><StatusBadge value={selected.sla_status} /></div>
                <div className="space-y-2 rounded-lg border border-border p-3 text-sm">
                  <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {selected.phone || "—"}</p>
                  <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {selected.email || "—"}</p>
                  <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {selected.location || "—"}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-muted-foreground">Company</p><p className="font-medium">{selected.company}</p></div>
                  <div><p className="text-xs text-muted-foreground">Source</p><p className="font-medium">{selected.source}</p></div>
                  <div><p className="text-xs text-muted-foreground">Business Unit</p><p className="font-medium">{selected.business_unit || "—"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Score</p><p className="font-medium">{selected.lead_score}</p></div>
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="lead-routing-info">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Routing</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-xs text-muted-foreground">Team</p><p className="font-medium">{selected.team || "—"}</p></div>
                    <div><p className="text-xs text-muted-foreground">Owner</p><p className="font-medium" data-testid="lead-owner">{ownerName(selected.owner_id)}</p></div>
                    <div><p className="text-xs text-muted-foreground">SLA</p><p className="font-medium">{selected.sla_minutes || 15} min</p></div>
                    {selected.meta_page && <div><p className="text-xs text-muted-foreground">Meta Page</p><p className="font-medium">{selected.meta_page}</p></div>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Update status</Label>
                  <Select value={selected.status} onValueChange={(v) => updateMut.mutate({ id: selected.id, body: { status: v } })}>
                    <SelectTrigger data-testid="lead-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {selected.next_action && <Badge variant="secondary">Next: {selected.next_action}</Badge>}
                <Button className="w-full rounded-full" onClick={() => convertMut.mutate(selected.id)} disabled={convertMut.isPending} data-testid="convert-lead-btn">
                  {convertMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Convert to Opportunity <ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
